import { createJsonClient } from '../../shared/httpClient';
import { useMocks } from '../../shared/env';
import { mockAcceptedEmployeeIds, mockInvitedEmployeeIds } from './mockInvitations';

/** The derived invitation state the Identity service reports per employee ("expired" is always computed from the expiry). */
export type InvitationState = 'none' | 'pending' | 'expired' | 'accepted' | 'revoked';

export interface EmployeeInvitationGateway {
  /** Per-employee invitation state for the caller's own company. An employee never invited is simply absent or 'none'. */
  listStates(employeeIds: readonly string[], signal?: AbortSignal): Promise<Record<string, InvitationState>>;
  /** Rotates the invitation (old link stops working) and sends a fresh email. `idempotencyKey` makes a repeated request safe. */
  resend(employeeId: string, idempotencyKey: string): Promise<void>;
}

interface InvitationStateItem { employeeId: string; state: InvitationState }

class HttpEmployeeInvitationGateway implements EmployeeInvitationGateway {
  // Invitations are owned by Identity (same origin, no service prefix), like work locations.
  private readonly request = createJsonClient('');

  async listStates(employeeIds: readonly string[], signal?: AbortSignal): Promise<Record<string, InvitationState>> {
    if (employeeIds.length === 0) return {};
    const query = new URLSearchParams({ employeeIds: employeeIds.join(',') });
    const result = await this.request<{ items: InvitationStateItem[] }>(`/api/employee-invitations?${query.toString()}`, undefined, signal);
    return Object.fromEntries(result.items.map((i) => [i.employeeId, i.state]));
  }

  async resend(employeeId: string, idempotencyKey: string): Promise<void> {
    await this.request(`/api/employee-invitations/${encodeURIComponent(employeeId)}/resend`, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }
}

/** Local-only stand-in used when the app runs on mocks: only employees invited through the Add Employee dialog in this session are pending. */
class MockEmployeeInvitationGateway implements EmployeeInvitationGateway {
  async listStates(employeeIds: readonly string[]): Promise<Record<string, InvitationState>> {
    const states: Record<string, InvitationState> = {};
    for (const id of employeeIds) {
      if (mockInvitedEmployeeIds.has(id)) states[id] = 'pending';
      else if (mockAcceptedEmployeeIds.has(id)) states[id] = 'accepted';
    }
    return states;
  }
  async resend(): Promise<void> { /* nothing to send in mock mode */ }
}

export const employeeInvitationGateway: EmployeeInvitationGateway = useMocks
  ? new MockEmployeeInvitationGateway()
  : new HttpEmployeeInvitationGateway();
